import { apiFetch } from "@/lib/api/client";
import type { SessionUser } from "@/lib/api/types";

/**
 * Entrar y salir.
 *
 * ## Esto se volvió mucho más simple que en Next
 *
 * Antes el login corría en el servidor: pedía a la API, leía las cabeceras
 * `Set-Cookie` de la respuesta y las volvía a emitir hacia el navegador a mano,
 * atributo por atributo. Todo ese reenvío existía porque quien hablaba con la
 * API no era el navegador.
 *
 * Ahora sí lo es, y sobre el mismo origen: el `Set-Cookie` del backend llega
 * derecho al navegador y se guarda solo. Se borraron unas cuarenta líneas de
 * manipulación de cookies que ya no tienen razón de existir, y con ellas la
 * clase de bug donde una cookie se reenvía perdiendo un atributo.
 *
 * Las cookies siguen siendo `httpOnly`: este código nunca las ve.
 */

export function loginWithPassword(username: string, password: string): Promise<SessionUser> {
  return apiFetch<SessionUser>("/api/v1/auth/login", {
    method: "POST",
    body: { username, password },
  });
}

/** Canjea el código que trae COLMENA en `/login?code=...`. */
export function exchangeSsoCode(code: string): Promise<SessionUser> {
  return apiFetch<SessionUser>(`/api/v1/auth/sso?code=${encodeURIComponent(code)}`, {
    method: "POST",
  });
}

/**
 * Cierra la sesión.
 *
 * Si la API falla, el error se traga a propósito: alguien que apretó «salir» y
 * se queda adentro es peor que alguien que sale de más. En una computadora
 * compartida, lo segundo es una molestia y lo primero es que el que sigue ve
 * todo.
 */
export async function logout(): Promise<void> {
  try {
    await apiFetch<void>("/api/v1/auth/logout", { method: "POST" });
  } catch {
    // La API caída no puede dejar a nadie encerrado en la aplicación.
  }
}
